<svelte:options
	customElement={{
		tag: "contact-form",
		shadow: "none",
	}}
/>

<script lang="ts">
	import { onMount } from "svelte";
	import ky from "ky";
	import { enforce, test, omitWhen } from "vest";
	import "vest/email";
	import { control, ValidatingForm } from "@wagich/form-validation";

	import { initFluentContext, Localized, localize } from "@/localization";
	import { trackEvent } from "@/analytics";
	import type { ContactRequest } from "@/models";
	import { FormState } from "@/utils";

	const initialFormData: ContactRequest = {
		fullname: "",
		email: "",
		phone: "",
		address: "",
		postalCode: "",
		city: "",
		message: "",
		subscribeToNewsletter: false,
		newsletterSegment: ""
	};

	let formState: FormState = $state(FormState.Ready);
	let token: string = "";

	initFluentContext();

	const form = new ValidatingForm<ContactRequest>(initialFormData, (data) => {
		test("fullname", localize("contactform-fullname.error-required"), () => {
			enforce(data.fullname).isNotBlank();
		});
		test("email", () => {
			enforce(data.email)
				.message(localize("contactform-email.error-required"))
				.isNotBlank()
				.message(localize("contactform-email.error-format"))
				.isEmail();
		});
		test("phone", localize("contactform-phone.error-required"), () => {
			enforce(data.phone).isNotBlank();
		});
		test("message", localize("contactform-message.error-required"), () => {
			enforce(data.message).isNotBlank();
		});

		omitWhen(!data.subscribeToNewsletter, () => {
			test("newsletterSegment", localize("contactform-segment.error-required"), () => {
				enforce(data.newsletterSegment).isNotBlank();
			});
		});
	});

	onMount(() => {
		let tokenElement = document.getElementById("RequestVerificationToken");
		if (tokenElement == null || tokenElement.textContent == null) {
			throw new Error("Could not load XSRF token.");
		}
		token = JSON.parse(tokenElement.textContent);
	});

	async function submitAsync() {
		if (formState !== FormState.Ready) {
			return;
		}
		if (!form.validate()) {
			return;
		}

		try {
			formState = FormState.Loading;
			await ky.post(`/api/contact`, {
				headers: { RequestVerificationToken: token },
				json: {
					sourcePath: window.location.pathname,
					sourceTitle: document.title,
					language: document.documentElement.lang,
					...form.getData()
				}
			});
			trackEvent("contactform_submit");

			formState = FormState.Success;
			form.reset();
		} catch (e) {
			formState = FormState.Error;
		}
	}
</script>

	<div class="contactform">
		<div class="title is-family-secondary has-text-primary">
			<Localized id="contactform-title" />
		</div>
		<div class="field">
			<label for="fullname" class="label is-small">
				<Localized id="contactform-fullname" />
			</label>
			<input id="fullname" class="input" type="text" {@attach control(form.fields.fullname)} autocomplete="name" />
			{#each form.fields.fullname.errors as error}
				<p class="help is-danger">{error}</p>
			{/each}
		</div>
		<div class="columns">
			<div class="column is-6">
				<div class="field">
					<label for="email" class="label is-small">
						<Localized id="contactform-email" />
					</label>
					<input id="email" class="input" type="email" {@attach control(form.fields.email)} autocomplete="email" />
					{#each form.fields.email.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
			</div>
			<div class="column is-6">
				<div class="field">
					<label for="phone" class="label is-small">
						<Localized id="contactform-phone" />
					</label>
					<input id="phone" class="input" type="text" {@attach control(form.fields.phone)} autocomplete="tel" />
					{#each form.fields.phone.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
			</div>
		</div>

		<div class="field">
			<label for="address" class="label is-small">
				<Localized id="contactform-address" />
			</label>
			<input id="address" class="input" type="text" {@attach control(form.fields.address)} autocomplete="address-line1" />
			{#each form.fields.address.errors as error}
				<p class="help is-danger">{error}</p>
			{/each}
		</div>
		<div class="columns">
			<div class="column is-4">
				<div class="field">
					<label for="postalCode" class="label is-small">
						<Localized id="contactform-postalcode" />
					</label>
					<input id="postalCode" class="input" type="text" {@attach control(form.fields.postalCode)} autocomplete="postal-code" />
					{#each form.fields.postalCode.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
			</div>
			<div class="column is-8">
				<div class="field">
					<label for="city" class="label is-small">
						<Localized id="contactform-city" />
					</label>
					<input id="city" class="input" type="text" {@attach control(form.fields.city)} autocomplete="address-level2" />
					{#each form.fields.city.errors as error}
						<p class="help is-danger">{error}</p>
					{/each}
				</div>
			</div>
		</div>

		<div class="field">
			<label for="message" class="label is-small">
				<Localized id="contactform-message" />
			</label>
			<textarea id="message" class="textarea" {@attach control(form.fields.message)}></textarea>
			{#each form.fields.message.errors as error}
				<p class="help is-danger">{error}</p>
			{/each}
		</div>

		<div class="field">
			<input type="checkbox" class="is-checkradio is-primary" {@attach control(form.fields.subscribeToNewsletter)} id="subscribeToNewsletter2" />
			<label class="checkbox" for="subscribeToNewsletter2">
				<Localized id="contactform-newsletter" />
			</label>
		</div>
		{#if form.fields.subscribeToNewsletter.value}
			<div class="field">
				<label class="label is-small">
					<Localized id="contactform-segment" />
				</label>
				<div class="control">
					<div class="mb-2">
						<input type="radio" class="is-checkradio is-primary" value="architect" id="newsletterSegmentArchitect2" {@attach control(form.fields.newsletterSegment)} />
						<label class="radio" for="newsletterSegmentArchitect2">
							<Localized id="contactform-segment-architect" />
						</label>
					</div>
					<div>
						<input type="radio" class="is-checkradio is-primary" value="consumer" id="newsletterSegmentConsumer2" {@attach control(form.fields.newsletterSegment)} />
						<label class="radio" for="newsletterSegmentConsumer2">
							<Localized id="contactform-segment-consumer" />
						</label>
					</div>
				</div>
				{#each form.fields.newsletterSegment.errors as error}
					<p class="help is-danger">{error}</p>
				{/each}
			</div>
		{/if}
		{#if formState === FormState.Ready || formState === FormState.Loading}
			<button type="button" class="button is-primary" class:is-loading={formState === FormState.Loading} onclick={submitAsync}>
				<Localized id="contactform-submit" />
			</button>
		{:else if formState === FormState.Success}
			<div class="notification is-success mt-0 pt-2">
				<Localized id="contactform-success" />
			</div>
		{:else if formState === FormState.Error}
			<div class="notification is-danger mt-0 pt-2">
				<Localized id="contactform-error" />
			</div>
		{/if}
	</div>
